import { useSuspenseQuery } from '@apollo/client/react';
import { useState, useTransition } from 'react';
import { debounce } from 'lodash';
import { Inbox, Plus, SearchX } from 'lucide-react';
import { useNavigate } from 'react-router';

import { IntegrationsDocument } from 'src/graphql/actions/integration/integrations.query';
import { withSuspense } from 'src/hoc';
import { useAddDeposit } from 'src/context/deposit';
import { InputSearch } from '@/components/ui/input-search';
import { Card, CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EmptyBlock } from '@/components/ui/empty-block';

import type { IIntegrationsQuery } from 'src/graphql/actions/integration/integrations.query';

import { IntegrationItem, IntegrationItemSkeleton } from './IntegrationItem';

const INTEGRATIONS_LIMIT = 100;

const methodTypeMap = {
  CustomIntegration: 'custom',
  OauthIntegration: 'oauth',
} as const;

const ActivateButton = ({
  text = 'Activate integration',
  size = 'default',
}: {
  text?: string;
  size?: 'sm' | 'default';
}) => {
  const navigate = useNavigate();

  const onActivate = () => navigate('/integrations');

  return (
    <Button size={size} onClick={onActivate}>
      <Plus /> {text}
    </Button>
  );
};

const ActivateNewIntegration = () => {
  return (
    <Card className="flex flex-col justify-between">
      <CardHeader className="items-center space-y-3">
        <div className="size-10 border border-dashed border-gray-200 rounded-lg flex items-center justify-center">
          <SearchX className="size-5 text-muted-foreground" />
        </div>
        <CardDescription className="text-xs text-center">Can’t see the integration you need?</CardDescription>
      </CardHeader>
      <CardFooter className="justify-center">
        <ActivateButton size="sm" />
      </CardFooter>
    </Card>
  );
};

const IntegrationListContainer = () => {
  const [query, setQuery] = useState('');
  const [isPending, startTransition] = useTransition();
  const { data } = useSuspenseQuery(IntegrationsDocument, {
    variables: { perPage: INTEGRATIONS_LIMIT, active: true, query },
  });
  const {
    ui: { selectMethod },
  } = useAddDeposit();

  // Filter out ShareFileIntegration as it's not supported
  const integrations = data.integrations.nodes.filter(
    ({ __typename }) => __typename !== 'ShareFileIntegration',
  ) as (IIntegrationsQuery['integrations']['nodes'][number] & {
    __typename: 'CustomIntegration' | 'OauthIntegration';
  })[];

  const onIntegrationClick = (integrationId: string) => {
    const integration = integrations.find((integration) => integration.id === integrationId);

    if (!integration) return;

    const { __typename, id, accountName, provider, tenantId } = integration;

    selectMethod(methodTypeMap[__typename], {
      id,
      name: accountName ?? '',
      platform: provider,
      ...(tenantId && { tenantId }),
    });
  };

  const onSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    startTransition(() => setQuery(e.target.value));
  }, 300);

  const onClearSearch = () => startTransition(() => setQuery(''));

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <h2 className="text-base font-bold">Through your active integrations</h2>
        <p className="text-sm text-muted-foreground">You can deposit through any integration you have access to.</p>
      </div>
      <InputSearch
        placeholder="Search active integrations"
        isLoading={isPending}
        onChange={onSearch}
        onClear={onClearSearch}
        className="max-w-[374px] bg-white"
      />
      {!integrations.length && !query && (
        <EmptyBlock
          title="No active Integrations found"
          description={`You don’t have any active integrations yet.\nGo to the Integrations page in the side menu to activate one.`}
          icon={Inbox}
          action={<ActivateButton />}
          withCard={false}
          classes={{
            title: 'text-sm font-semibold',
            content: 'min-h-[280px] flex flex-col justify-center',
          }}
        />
      )}
      {!integrations.length && query && (
        <EmptyBlock
          title="No integrations match your search."
          description="Add a new integration if the one you need isn’t available."
          icon={SearchX}
          action={<ActivateButton text="Add new" />}
          withCard={false}
          classes={{
            title: 'text-base',
            content: 'min-h-[280px] flex flex-col justify-center',
          }}
        />
      )}
      {Boolean(integrations.length) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ActivateNewIntegration />
          {integrations.map((integration) => (
            <IntegrationItem key={integration.id} integration={integration} onClick={onIntegrationClick} />
          ))}
        </div>
      )}
    </div>
  );
};

const IntegrationListSkeleton = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <IntegrationItemSkeleton />
      <IntegrationItemSkeleton />
      <IntegrationItemSkeleton />
      <IntegrationItemSkeleton />
    </div>
  );
};

export default withSuspense(IntegrationListContainer, IntegrationListSkeleton);
